import React, { ComponentType, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
// @ts-ignore
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { AppState } from '@om-fe/core/core/types';
import { DisciplineRouteType, getDisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { getMainMarketsLimit, getMainMarketsLimitByWidth } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { PlatformTypes, isBrowser, replaceTemplateStringPlaceholders } from '@om-fe/utils';
import { RouteNames, RouteDetails, EMRoutingContextInterface, EMRoutingContext} from '@om-fe/core/core/routing/utils';
import getConfig, { EnvConfig, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { MAIN_MARKET_MIN_LIMIT } from '@om-fe/core/components/match-list/utils/match-list.constants';
import { UseSportsNavStore, useSportsNavTopicStoreCreator } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.hooks';
import { SportsNav } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav';
import { SportsNavType, SportsNavCounterType } from '@om-fe/widgets/dist/widgets/navs/sports-nav/sports-nav.constants';
import { LayoutType } from '@om-fe/widgets/dist/components/nav/nav.constants';
import { SportRouteParams } from '@om-fe/widgets/dist/common/routing/routing.constants';
import { setVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import CategoryNav, { CategoryNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/category-nav/category-nav';
import { navBem } from '@om-fe/widgets/dist/components/nav/nav.utils';
import { MixedGroupedTournament } from '../components/mixed-grouped/containers/mixed-grouped-tournament/mixed-grouped-tournament';
import { DisciplineLiveStatus, DisciplinesType, TournamentParams, isOutrightDiscipline } from '@om-fe/models';
import getSliceName from '@om-fe/core/utils/get-slice-name';
import getSlice from '@om-fe/core/utils/get-slice';
import { RoutingModel } from '@om-fe/core/store/routing';
import { getDisciplineRouteDetails } from '@om-fe/core/components/discipline-anchor/discipline-anchor.utils';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { useEMRouting } from '@om-fe/core/core/routing/with-routing';
import { useReady } from '@om-fe/store';
import { categoryNavSlice, CategoryNavStateModel } from '@om-fe/core/components/nav-secondary/category-nav/category-nav-store';
import { ParentNavItemRouteParamsMap } from '@om-fe/core/components/nav-secondary/parent-nav/parent-nav-item';
import { SportsEventCategoryNavItemModel } from '@om-fe/widgets/dist/widgets/sports-menu/event-navigation-menu/event-navigation-menu.types';
import { MixedGroupedOddsOutrightsTabSelected } from '../components/mixed-grouped/components/tabs/mixed-grouped-odds-outrights-tabs.types';
import { useHistory } from 'react-router-dom';
import { History } from 'history';
import { CustomContentLoadLazy } from '@om-fe/core/core/widgets/omfe-widgets';
import { CustomContentType } from '@om-fe/core/core/widgets/omfe-widgets/model/omfe-widgets-model';
import SportAllLocationContent from '../components/top-bets/components/all-location-content';

interface SportCategoryPageStoreProps {
    readonly isLarge?: boolean;
    readonly breakpoints?: Breakpoints;
    readonly deviceType?: PlatformTypes;
    readonly routing: RoutingModel;
    readonly firstItemCategory: SportsEventCategoryNavItemModel;
    readonly isLoggedIn: boolean;
}

export type SportCategoryPageProps = SportCategoryPageStoreProps 
    & RouteComponentProps<{
        readonly disciplineId: string;
        readonly disciplineName: string;
        readonly tournamentName: string;
        readonly tournamentId: string;
        readonly eventCategoryId?: string;
        readonly eventCategoryName?: string;
        readonly locationName?: string;
        readonly locationId?: string;
        readonly activeTab?: string;
        readonly bettingOfferIds?: string;
        readonly stake?: string;
        readonly sportLocationName?: string;
        readonly sportLocationId?: string;
}>;

function SportCategoryPageComponent(
    {
        match: { 
            params: {
                disciplineId,
                disciplineName,
                eventCategoryId,
                sportLocationId,
                eventCategoryName,
            },
        },
        isLarge,
        breakpoints,
        deviceType,
        firstItemCategory,
        isLoggedIn,
    }: SportCategoryPageProps,
) {
    let sportPageType: DisciplineRouteType;
    const config: OMFE2ConfigModel = getConfig();
    const topicTimezoneOffset: boolean = config.matchListConfig?.topBets?.topicTimezoneOffset ?? false;
    const enabledBetSimulate: boolean = config.simulate?.enabled ?? false;

    const [tournamentId, setTournamentId] = useState('0');
    const useStore: UseSportsNavStore = useSportsNavTopicStoreCreator({
        sportsNavType: SportsNavType.All,
        counterType: SportsNavCounterType.NumberOfEvents,
        showFromAPI: true,
        topicTimezoneOffset,
        enabledBetSimulate,
    });
    const ready: boolean = useReady(useStore);
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
    const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined);
    const { gettext, selectedLanguage } = useI18NContext();
    const { getFullPath } = useEMRouting();
    const enabledDisciplinePage : boolean = config?.enabledDisciplinePage ?? false;
    const { getPath } = useContext<EMRoutingContextInterface>(EMRoutingContext);
    const history: History = useHistory();
    const displayAllTab: boolean = !!config.locationNavConfig?.displayAllTab && !isOutrightDiscipline(disciplineId) && (disciplineId !== DisciplinesType.PriceBoost);
    const isShowAllLocationContent: boolean =
        !!(
            eventCategoryId === '0' && displayAllTab &&
            !isOutrightDiscipline(disciplineId)
        );

    const mainMarketsLimit: number = config.mainMarketsLimit?.responsive
        ? config.mainMarketsLimit?.byWidth && (isBrowser() && window)
            ? getMainMarketsLimitByWidth(window.innerWidth, config.mainMarketsLimit?.breakpointsByWidth)
            : getMainMarketsLimit(breakpoints)
        : MAIN_MARKET_MIN_LIMIT;

    const generateSportsPageUrl = useCallback((params: SportRouteParams): string | undefined => {
        const routeDetails: RouteDetails = getDisciplineRouteDetails({
            childId: params.childSportId,
            childName: params.childSportName,
            disciplineId: params.sportId,
            disciplineName: params.sportName,
            isLive: false,
            disciplineRouteType: getDisciplineRouteType(params),
            gettext,
        });

        return getFullPath(routeDetails.routeName, routeDetails.routeParams);
    }, [getFullPath, gettext]);
    
    const handleCategory = (CategoryId: SetStateAction<string | undefined>) => {
        setActiveCategory(CategoryId);
    }

    const handleSetTournamentParams = useCallback((data: TournamentParams) => {
        setTournamentId(data.tournamentId);
        setActiveTab(data.activeTab);
        setVirtualStateExtraParams(data);
    }, []);

    const handleActiveTabOutrights = useCallback(() => {
        setActiveTab(MixedGroupedOddsOutrightsTabSelected.Outrights);
    }, []);

    const seoUrl = useMemo((): string | undefined => {
        const url: string | undefined = config.customContent?.seoContent?.url;

        if (!url) {
            return undefined;
        }
            
        const env: EnvConfig = config.customConfig?.env ?? EnvConfig.Stage;
        const userRoles: string = isLoggedIn ? 'loggedIn' : 'everyone';
        
        return replaceTemplateStringPlaceholders(url, {selectedLanguage, env, userRoles, disciplineName});
    }, [config.customConfig?.env, config.customContent?.seoContent?.url, disciplineName, isLoggedIn, selectedLanguage]);
    
    const payloads: string | undefined = config.customContent?.seoContent?.payloads;
    const routingUrl: string = `/${selectedLanguage}/sport/${disciplineName}-${disciplineId}/${eventCategoryName}-${eventCategoryId}/category`;
    
    useEffect(() => {
        setActiveTab(undefined);
        setTournamentId('0');
    }, [disciplineId, eventCategoryId]);

    const routeParamsMap: CategoryNavItemRouteParamsMap | ParentNavItemRouteParamsMap = {
        disciplineId: 'disciplineId',
        disciplineName: 'disciplineName',
        eventCategoryId: 'eventCategoryId',
        eventCategoryName: 'eventCategoryName',
    };

    const getTournamentParams: TournamentParams = {
        activeTab: activeTab,
        tournamentId: tournamentId,
        disciplineId: disciplineId,
        disciplineName: disciplineName,
        eventCategoryId: eventCategoryId,
    };

    if (ready && eventCategoryId === '0' && firstItemCategory?.id && isBrowser() && !displayAllTab) {
        const isHyphenatedFullUrl: boolean = !!getConfig().enableHyphenatedUrl?.fullName;
        const eventCategoryName = isHyphenatedFullUrl ? firstItemCategory?.name : firstItemCategory?.shortName;

        history.replace(getPath(RouteNames.sportCategory, {
            disciplineName,
            disciplineId,
            eventCategoryName,
            eventCategoryId: firstItemCategory?.id,
        }));
    }

    return (
        <div>
            <Breadcrumbs
                disciplineId={disciplineId}
                locationId={sportLocationId}
                tournamentId={tournamentId !== '0' ? tournamentId : undefined}
                categoryId={eventCategoryId}
                isLarge={isLarge}
                routeType={sportPageType}
            />
            {ready &&
                <div className={'NavWrapper'}>
                    <SportsNav
                        activeItemId={disciplineId}
                        sportRoute={generateSportsPageUrl}
                        useStore={useStore}
                        className={`${SportsNavType.Popular}SportsNav`}
                        showIcon
                        hasArrows={deviceType === 'desktop'}
                        layoutType={LayoutType.Main}
                    />
                </div>
            }
            <div className={navBem()}>
                <CategoryNav
                    disciplineId={disciplineId}
                    disciplineName={disciplineName}
                    liveStatus={DisciplineLiveStatus.NOT_LIVE}
                    routeName={RouteNames.sportCategory}
                    routeParamsMap={routeParamsMap}
                    genericId={activeCategory}
                    preventRedirect={false}
                    noRedirectFilter={handleCategory}
                    instanceId={`CategoryNavSlice`}
                    hasArrows={deviceType === 'desktop'}
                    hideAll={!displayAllTab}
                />
            </div>
            {eventCategoryId && !isShowAllLocationContent && (
                <MixedGroupedTournament
                    disciplineId={disciplineId}
                    locationId={sportLocationId}
                    tournamentId={tournamentId}
                    sportPageType={DisciplineRouteType.Location}
                    eventCategoryId={eventCategoryId}
                    activeTab={activeTab}
                    mainMarketsLimit={mainMarketsLimit}
                    deviceType={deviceType}
                    handleTournamentParams={handleSetTournamentParams}
                    tournamentParams={getTournamentParams}
                    handleActiveTabOutrights={handleActiveTabOutrights}
                    navLayout={enabledDisciplinePage}
                />
            )} 
            {isShowAllLocationContent &&
                <SportAllLocationContent
                    instanceId={`All-${'Sport-' + disciplineId}-Custom`}
                    disciplineId={disciplineId}
                    locationId={tournamentId}
                    breakpoints={breakpoints} 
                    mainMarketsLimit={mainMarketsLimit}
                />
            } 
            {seoUrl &&
                <CustomContentLoadLazy
                    url={seoUrl}
                    contentType={CustomContentType.NORMAL}
                    payloads={payloads}
                    filterBy={[['route', routingUrl]]}
                />
            }
        </div>
    );
};

const SportCategory: ComponentType<SportCategoryPageStoreProps> = connect<SportCategoryPageStoreProps, null, SportCategoryPageProps, AppState>(
    (state: AppState): SportCategoryPageStoreProps => {
        const sliceName: string = getSliceName(categoryNavSlice, 'CategoryNavSlice');
        const slice: CategoryNavStateModel | undefined = getSlice<CategoryNavStateModel>(state, sliceName);
        const firstIdCategory = slice?.parts?.notLive?.list[0];

        return {
            isLarge: (state.breakpoints.large || state.breakpoints.xl),
            breakpoints: state.breakpoints,
            deviceType: state.deviceInfo?.platform.type,
            routing: state.routing,
            firstItemCategory: slice?.parts?.notLive?.items[firstIdCategory],
            isLoggedIn: !!state.session.info,
        };
    })(SportCategoryPageComponent);
export default SportCategory;
